import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast, Toaster } from 'sonner';
import { Copy, Download, FolderOpen, Link2, Loader2, Mail, Pause, Pencil, Play, RefreshCw } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { DocumentacionAdmin, EstadoDocBadge } from '../../components/actualizacion-familiar/DocumentacionAdmin';
import {
  CICLOS, FormularioFamiliar, payloadRespuesta, respuestaAEdicion, validarRespuesta,
} from '../../components/actualizacion-familiar/FormularioFamiliar';

const API = process.env.REACT_APP_BACKEND_URL + '/api/actualizacion-familiar/admin';

const LinkCard = ({ config, onChange }) => {
  const url = config.token ? `${window.location.origin}/campus/actualizacion-familiar/${config.token}` : '';
  const generar = async () => {
    if (config.token && !window.confirm('Se generará un link nuevo y el anterior dejará de funcionar. ¿Continuar?')) return;
    onChange((await axios.post(`${API}/config/generar`)).data);
    toast.success('Link generado');
  };
  const pausar = async () => onChange((await axios.patch(`${API}/config`, { activo: !config.activo })).data);
  return (
    <div className="rounded-xl border bg-white p-5 space-y-3" data-testid="af-link-card">
      <div className="flex items-center gap-2">
        <Link2 className="h-5 w-5 text-primary" />
        <h2 className="font-semibold">Link del formulario</h2>
        {config.token && (
          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${config.activo ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-testid="af-link-estado">
            {config.activo ? 'Activo' : 'Pausado'}
          </span>
        )}
      </div>
      {url ? (
        <div className="flex flex-col sm:flex-row gap-2">
          <code className="flex-1 truncate rounded bg-gray-100 px-3 py-2 text-xs" data-testid="af-link-url">{url}</code>
          <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copiado'); }} data-testid="af-copiar-link-btn"><Copy className="h-4 w-4 mr-1" />Copiar</Button>
          <Button variant="outline" size="sm" onClick={pausar} data-testid="af-pausar-link-btn">
            {config.activo ? <><Pause className="h-4 w-4 mr-1" />Pausar</> : <><Play className="h-4 w-4 mr-1" />Reactivar</>}
          </Button>
        </div>
      ) : <p className="text-sm text-gray-500">Todavía no generaste el link.</p>}
      <Button size="sm" variant={url ? 'ghost' : 'default'} onClick={generar} data-testid="af-generar-link-btn">
        <RefreshCw className="h-4 w-4 mr-1" />{url ? 'Generar link nuevo' : 'Generar link'}
      </Button>
    </div>
  );
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl border bg-white p-4" data-testid={testid}>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    <p className="text-xs text-gray-500 mt-1">{label}</p>
  </div>
);

const Estadisticas = ({ s }) => (
  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
    <Stat label="Completaron el formulario" value={s.completados} testid="af-stat-completados" />
    <Stat label="Interesados en el programa" value={s.interesados} testid="af-stat-interesados" />
    <Stat label="Con hijos en edad escolar" value={s.con_hijos} testid="af-stat-con-hijos" />
    <Stat label="Hijos de interesados" value={s.hijos_interesados} testid="af-stat-hijos" />
    <div className="col-span-2 lg:col-span-4 flex flex-wrap gap-2" data-testid="af-stat-ciclos">
      {Object.entries(CICLOS).map(([k, l]) => (
        <span key={k} className="text-xs rounded-full bg-primary/10 text-primary px-3 py-1">{l}: <b>{s.hijos_por_ciclo?.[k] || 0}</b></span>
      ))}
    </div>
  </div>
);

const Fila = ({ f, onEditar, onDocs }) => {
  const r = f.respuesta;
  return (
    <tr className="border-t align-top" data-testid={`af-fila-${f.trabajador_id}`}>
      <td className="p-3"><p className="font-medium">{f.apellido}, {f.nombre}</p><p className="text-xs text-gray-500">Mat. {f.matricula} · DNI {f.documento}</p></td>
      <td className="p-3 text-xs text-gray-600"><p>{f.email}</p><p>{f.celular}</p></td>
      <td className="p-3">
        {r.tiene_hijos ? r.hijos.map((h, i) => (
          <p key={i} className="text-xs">{h.nombre_apellido} · DNI {h.dni} · {h.edad_2027} años · {CICLOS[h.ciclo]}</p>
        )) : <span className="text-xs text-gray-400">Sin hijos en edad escolar</span>}
        {r.posee_cud && <span className="mt-1 inline-block text-xs rounded-full bg-blue-100 text-blue-700 px-2 py-0.5" data-testid={`af-cud-${f.trabajador_id}`}>Con CUD</span>}
      </td>
      <td className="p-3 text-xs">{r.interes_programa ? 'Sí' : 'No'}</td>
      <td className="p-3">
        {r.interes_programa ? (
          <button onClick={() => onDocs(f)} className="flex items-center gap-1" data-testid={`af-docs-${f.trabajador_id}`}>
            <EstadoDocBadge estado={f.doc_estado} /><FolderOpen className="h-4 w-4 text-gray-400" />
          </button>
        ) : <span className="text-xs text-gray-300">—</span>}
      </td>
      <td className="p-3"><Button size="sm" variant="ghost" onClick={() => onEditar(f)} data-testid={`af-editar-${f.trabajador_id}`}><Pencil className="h-4 w-4" /></Button></td>
    </tr>
  );
};

const EditarDialog = ({ fila, onClose, onSaved }) => {
  const [form, setForm] = useState(() => respuestaAEdicion(fila.respuesta));
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const guardar = async () => {
    const m = validarRespuesta(form);
    if (m) return setErr(m);
    setSaving(true);
    try {
      await axios.put(`${API}/respuestas/${fila.trabajador_id}`, payloadRespuesta(form));
      toast.success('Respuesta actualizada');
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.detail?.toString() || 'No se pudo guardar');
    } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="af-editar-dialog">
        <DialogHeader><DialogTitle>Editar respuesta · {fila.apellido}, {fila.nombre}</DialogTitle></DialogHeader>
        <FormularioFamiliar value={form} onChange={setForm} />
        {err && <p className="text-sm text-red-600" data-testid="af-editar-error">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={guardar} disabled={saving} data-testid="af-editar-guardar-btn">{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Guardar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const DocsDialog = ({ fila, onClose }) => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="af-docs-dialog">
      <DialogHeader><DialogTitle>Documentación · {fila.apellido}, {fila.nombre}</DialogTitle></DialogHeader>
      <DocumentacionAdmin trabajadorId={fila.trabajador_id} />
    </DialogContent>
  </Dialog>
);

const SolicitarDialog = ({ onClose, onDone }) => {
  const [mensaje, setMensaje] = useState('');
  const [reenviar, setReenviar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    try {
      const r = (await axios.post(`${API}/documentacion/solicitar`, { mensaje, reenviar })).data;
      toast.success(`Pedido registrado para ${r.destinatarios} interesades. Los emails se están enviando (${r.enviando}).`);
      if (r.sin_email.length) toast.warning(`Sin email cargado: ${r.sin_email.join('; ')}`, { duration: 15000 });
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo enviar'); } finally { setEnviando(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="af-solicitar-dialog">
        <DialogHeader><DialogTitle>Pedir documentación a los interesados</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-600">Se envía un email a cada interesade con un botón para cargar la documentación que acredite el vínculo. Lo que suban queda anexado a su perfil.</p>
        <Textarea placeholder="Mensaje adicional (opcional), por ejemplo una fecha límite" value={mensaje} onChange={(e) => setMensaje(e.target.value)} data-testid="af-solicitar-mensaje" />
        <label className="flex items-start gap-2 text-sm">
          <Checkbox checked={reenviar} onCheckedChange={(v) => setReenviar(!!v)} data-testid="af-solicitar-reenviar" />
          <span>Reenviar también a quienes ya se les pidió y todavía no cargaron (o se les rechazó)</span>
        </label>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={enviar} disabled={enviando} data-testid="af-solicitar-enviar-btn">{enviando && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Enviar pedido</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function AdminProgramaEscolarPage() {
  const [config, setConfig] = useState({});
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [soloInteresados, setSoloInteresados] = useState(true);
  const [editando, setEditando] = useState(null);
  const [docsDe, setDocsDe] = useState(null);
  const [solicitando, setSolicitando] = useState(false);
  const [exportando, setExportando] = useState(false);

  const cargar = useCallback(async () => {
    const [c, s, r] = await Promise.all([
      axios.get(`${API}/config`), axios.get(`${API}/estadisticas`),
      axios.get(`${API}/respuestas`, { params: { solo_interesados: soloInteresados } }),
    ]);
    setConfig(c.data); setStats(s.data); setItems(r.data.items);
  }, [soloInteresados]);

  useEffect(() => { cargar().catch(() => toast.error('No se pudieron cargar los datos')); }, [cargar]);

  const exportar = async () => {
    setExportando(true);
    try {
      const r = await axios.get(`${API}/exportar`, { responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = 'programa_escolar_2027.xlsx';
      a.click();
    } catch { toast.error('No se pudo exportar'); } finally { setExportando(false); }
  };

  return (
    <div className="space-y-6" data-testid="admin-programa-escolar-page">
      <Toaster position="top-right" richColors />
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Programa Escolar 2027</h1>
        <p className="text-sm text-gray-500">Respuestas del Formulario de Actualización Familiar.</p>
      </div>
      <LinkCard config={config} onChange={setConfig} />
      {stats && <Estadisticas s={stats} />}
      <div className="rounded-xl border bg-white">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 border-b">
          <div className="flex gap-1 rounded-full bg-gray-100 p-1 w-fit">
            {[[true, 'Interesados'], [false, 'Todas las respuestas']].map(([v, l]) => (
              <button key={l} onClick={() => setSoloInteresados(v)} data-testid={`af-filtro-${v ? 'interesados' : 'todas'}`}
                className={`px-4 py-1.5 rounded-full text-sm ${soloInteresados === v ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>{l}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => setSolicitando(true)} size="sm" data-testid="af-solicitar-doc-btn"><Mail className="h-4 w-4 mr-1" />Pedir documentación</Button>
            <Button onClick={exportar} disabled={exportando} variant="outline" size="sm" data-testid="af-exportar-btn">
              {exportando ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Exportar interesados a Excel
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]" data-testid="af-tabla">
            <thead className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
              <tr><th className="p-3">Matriculade</th><th className="p-3">Contacto</th><th className="p-3">Hijos 2027</th><th className="p-3">Interés</th><th className="p-3">Documentación</th><th className="p-3" /></tr>
            </thead>
            <tbody>
              {items.map((f) => <Fila key={f.trabajador_id} f={f} onEditar={setEditando} onDocs={setDocsDe} />)}
              {!items.length && <tr><td colSpan={6} className="p-8 text-center text-gray-400" data-testid="af-tabla-vacia">Todavía no hay respuestas.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {editando && <EditarDialog fila={editando} onClose={() => setEditando(null)} onSaved={() => { setEditando(null); cargar(); }} />}
      {docsDe && <DocsDialog fila={docsDe} onClose={() => { setDocsDe(null); cargar(); }} />}
      {solicitando && <SolicitarDialog onClose={() => setSolicitando(false)} onDone={() => { setSolicitando(false); cargar(); }} />}
    </div>
  );
}
